<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

import { useAlert } from 'dashboard/composables';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import BaseButton from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const store = useStore();
const { t } = useI18n();

const invitedIds = ref([]);

const currentUserId = computed(() => store.getters.getCurrentUserID);
const onlineContacts = computed(
  () => store.getters['proactiveChat/getOnlineContacts']
);
const uiFlags = computed(() => store.getters['proactiveChat/getUIFlags']);
const isAutoGreetingEnabled = computed(
  () => store.getters['proactiveChat/isAutoGreetingEnabled']
);
const isFetching = computed(() => uiFlags.value.isFetching);
const isSending = computed(() => uiFlags.value.isSending);

const isInvited = contactId => invitedIds.value.includes(contactId);

const handleSendInvitation = async contact => {
  const result = await store.dispatch('proactiveChat/sendInvitation', {
    contactInboxId: contact.contact_inbox_id,
  });

  if (result.success) {
    invitedIds.value.push(contact.id);
    useAlert(t('ONLINE_VISITORS.INVITATION_SENT'));
    return;
  }

  useAlert(t('ONLINE_VISITORS.INVITATION_FAILED'));
};

const handleInviteAll = async () => {
  const result = await store.dispatch('proactiveChat/inviteAll');
  if (result.success && result.queued) {
    invitedIds.value = onlineContacts.value.map(contact => contact.id);
    useAlert(t('ONLINE_VISITORS.INVITATIONS_QUEUED', { count: result.count }));
    return;
  }

  useAlert(t('ONLINE_VISITORS.INVITATIONS_FAILED'));
};

const toggleAutoGreeting = () => {
  store.dispatch(
    'proactiveChat/setAutoGreetingEnabled',
    !isAutoGreetingEnabled.value
  );
};

onMounted(() => {
  store.dispatch('proactiveChat/startOnlineContactsWatcher');
  store.dispatch('proactiveChat/fetchAutoGreetingStatus');
});

onBeforeUnmount(() => {
  store.dispatch('proactiveChat/stopOnlineContactsWatcher');
});
</script>

<template>
  <section class="w-full max-w-5xl mx-auto">
    <header
      class="flex items-center justify-between gap-4 pb-4 border-b border-n-weak"
    >
      <div class="flex items-center min-w-0 gap-2">
        <h1 class="text-lg font-semibold text-n-slate-12">
          {{ t('ONLINE_VISITORS.TITLE') }}
        </h1>
        <span
          class="inline-flex items-center justify-center h-5 min-w-5 px-1.5 text-xs font-medium text-white rounded-full bg-n-brand"
        >
          {{ onlineContacts.length }}
        </span>
      </div>
      <BaseButton
        :disabled="isSending || onlineContacts.length === 0"
        :is-loading="isSending"
        size="sm"
        color="blue"
        variant="solid"
        icon="i-lucide-users-round"
        :label="t('ONLINE_VISITORS.INVITE_ALL')"
        @click="handleInviteAll"
      />
    </header>

    <div
      v-if="currentUserId === 1"
      class="flex items-center justify-between gap-4 py-4 border-b border-n-weak"
    >
      <div class="min-w-0">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('ONLINE_VISITORS.AUTO_GREETING.TITLE') }}
        </h2>
        <p class="mt-0.5 text-sm text-n-slate-11 break-words">
          {{ t('ONLINE_VISITORS.AUTO_GREETING.MESSAGE') }}
        </p>
      </div>
      <Switch
        :model-value="isAutoGreetingEnabled"
        @change="toggleAutoGreeting"
      />
    </div>

    <div
      v-if="isFetching"
      class="flex items-center justify-center min-h-40 text-sm text-n-slate-11"
    >
      {{ t('ONLINE_VISITORS.LOADING') }}
    </div>

    <div
      v-else-if="onlineContacts.length === 0"
      class="flex flex-col items-center justify-center min-h-40 gap-2 text-center text-n-slate-11"
    >
      <span class="i-lucide-users size-5" aria-hidden="true" />
      <p class="text-sm">{{ t('ONLINE_VISITORS.EMPTY') }}</p>
    </div>

    <ul v-else class="divide-y divide-n-weak">
      <li
        v-for="contact in onlineContacts"
        :key="contact.id"
        class="flex items-center justify-between min-h-16 gap-4 py-3"
      >
        <div class="flex items-center min-w-0 gap-3">
          <Avatar
            :src="contact.avatar_url"
            :name="contact.name || t('ONLINE_VISITORS.ANONYMOUS')"
            :size="36"
            status="online"
            rounded-full
          />
          <div class="min-w-0">
            <p class="text-sm font-medium truncate text-n-slate-12">
              {{ contact.name || t('ONLINE_VISITORS.ANONYMOUS') }}
            </p>
            <p v-if="contact.email" class="text-xs truncate text-n-slate-11">
              {{ contact.email }}
            </p>
          </div>
        </div>
        <BaseButton
          v-tooltip.left="
            isInvited(contact.id)
              ? t('ONLINE_VISITORS.INVITED')
              : t('ONLINE_VISITORS.INVITE')
          "
          :disabled="isSending || isInvited(contact.id)"
          size="sm"
          :color="isInvited(contact.id) ? 'slate' : 'blue'"
          variant="ghost"
          :icon="
            isInvited(contact.id)
              ? 'i-lucide-check'
              : 'i-lucide-message-circle-plus'
          "
          :aria-label="
            isInvited(contact.id)
              ? t('ONLINE_VISITORS.INVITED')
              : t('ONLINE_VISITORS.INVITE')
          "
          @click="handleSendInvitation(contact)"
        />
      </li>
    </ul>
  </section>
</template>
